/**
 * The email newsletter's subscriber list, in words (newsletter-subscribers,
 * 2026-10-24; build-output/architecture/email-campaigns.md → "Subscribers"):
 * the `/campaigns` Subscribers tab, and the member page's Newsletter row,
 * which says when and how the member joined by the same words. Pure: the
 * pages render what these return, and `newsletter-subscribers.test.ts` pins
 * them.
 *
 * The wire shapes mirror the output of `list_newsletter_subscribers`
 * (`packages/canonical-actions/src/actions/list-newsletter-subscribers.ts`).
 */

import type {
  MarketingExclusionReason,
  MarketingGoal,
  MarketingLifecycle,
} from "@layer/canonical-data/marketing-audience";
import type {
  NewsletterJoinedVia,
  NewsletterSubscriberTotals,
  NewsletterSubscriberView,
} from "@layer/canonical-data/newsletter-subscribers";

// ---------------------------------------------------------------------------
// Wire shapes.
// ---------------------------------------------------------------------------

export interface NewsletterSubscriber {
  readonly member_id: string;
  readonly display_name: string | null;
  readonly email: string | null;
  readonly joined_at: string;
  readonly joined_via: NewsletterJoinedVia;
  readonly on_list: boolean;
  readonly left_out_reason: MarketingExclusionReason | null;
  readonly lifecycle: MarketingLifecycle;
  readonly goal: MarketingGoal;
  readonly whatsapp_reachable: boolean;
  readonly newsletters_sent: number;
  readonly last_newsletter_at: string | null;
  readonly next_newsletter_at: string | null;
  readonly newsletters_opened: number;
  readonly newsletters_clicked: number;
  readonly newsletter_unsubscribed_at: string | null;
  readonly welcome_sent_at: string | null;
  readonly whatsapp_newsletters_sent: number;
  readonly last_whatsapp_newsletter_at: string | null;
}

export interface NewsletterSubscribersList {
  readonly as_of: string;
  readonly totals: NewsletterSubscriberTotals;
  readonly matched: number;
  readonly subscribers: ReadonlyArray<NewsletterSubscriber>;
  readonly next_offset: number | null;
}

export interface NewsletterSubscribersInput {
  readonly view: NewsletterSubscriberView;
  readonly search?: string;
  readonly limit: number;
  readonly offset: number;
}

// ---------------------------------------------------------------------------
// Words.
// ---------------------------------------------------------------------------

/** The filter chips, in order. */
export const SUBSCRIBER_VIEW_LABEL: Readonly<Record<NewsletterSubscriberView, string>> = {
  on_list: "On the list",
  signed_up: "Signed up",
  left_out: "Left out",
};

/** The "How" column. */
export const JOINED_VIA_LABEL: Readonly<Record<NewsletterJoinedVia, string>> = {
  newsletter_form: "Newsletter form",
  signed_up: "Signed up as a member",
  member: "As a member",
};

/** What the "How" column's hover says. */
export const JOINED_VIA_HINT: Readonly<Record<NewsletterJoinedVia, string>> = {
  newsletter_form: "A newsletter form on the website created their record",
  signed_up:
    "Signed up while already a member: a newsletter form on the website, or staff's Re-subscribe",
  member: "Never signed up: the newsletter reaches every member who has not opted out",
};

/**
 * When and how the member joined, as one line: the member page's Newsletter
 * row. `day` formats a date (the studio's own zone on the page).
 */
export function joinedLine(
  join: { readonly at: string; readonly via: NewsletterJoinedVia },
  day: (iso: string) => string,
): string {
  switch (join.via) {
    case "newsletter_form":
      return `Signed up on the website, ${day(join.at)}`;
    case "signed_up":
      return `Signed up ${day(join.at)}, already a member`;
    case "member":
      return `On it as a member since ${day(join.at)}; never signed up`;
  }
}

/**
 * The "Newsletters" cell: how many went, and how many were opened and
 * clicked; `—` when none went. Opens over-count (Apple Mail opens every email
 * it delivers), so clicks are the firmer signal.
 */
export function newslettersCell(s: Pick<
  NewsletterSubscriber,
  "newsletters_sent" | "newsletters_opened" | "newsletters_clicked"
>): string {
  if (s.newsletters_sent === 0) return "—";
  return `${String(s.newsletters_sent)} sent · ${String(s.newsletters_opened)} opened · ${String(s.newsletters_clicked)} clicked`;
}

/**
 * The rows after "Load more": the next page appended to those shown. An offset
 * page over a list that moved since the last one (someone joined) repeats a
 * row already shown; it is kept once, where it first appeared.
 */
export function appendSubscriberPage(
  shown: ReadonlyArray<NewsletterSubscriber>,
  page: ReadonlyArray<NewsletterSubscriber>,
): ReadonlyArray<NewsletterSubscriber> {
  const seen = new Set(shown.map((s) => s.member_id));
  return [...shown, ...page.filter((s) => !seen.has(s.member_id))];
}

/** The tiles above the list, in order. */
export function subscriberTiles(
  t: NewsletterSubscriberTotals,
): ReadonlyArray<{ readonly label: string; readonly value: number; readonly hint: string }> {
  return [
    {
      label: "On the list",
      value: t.on_list,
      hint: `of ${t.active_members.toLocaleString("en-GB")} active members`,
    },
    { label: "Joined in the last 7 days", value: t.joined_last_7_days, hint: "on the list now" },
    { label: "Joined in the last 30 days", value: t.joined_last_30_days, hint: "on the list now" },
    { label: "Signed up", value: t.signed_up, hint: "on a newsletter form, or re-subscribed" },
    { label: "Left out", value: t.left_out, hint: "active members it does not reach" },
  ];
}
